{
  "name": "Huynh Phuong Duy",
  "title": "Full-Stack Developer",
  "location": "Thu Duc, Ho Chi Minh City, Viet Nam",
  "summary": "I have built multiple projects from the ground up and can work effectively both independently and collaboratively. I am a fast and eager learner who adapts quickly to new environments, a committed team player, and a curiosity-driven problem solver.",
  "highlight": "I built ETL pipelines with 550+ connectors, reviewed 100+ merge requests, raised a job site's PageSpeed score from 75 to 98, and shipped a CV builder that had 1,000+ active users at launch.",
  "education": [
    {
      "school": "Bach Khoa University HCMC",
      "major": "Computer Science",
      "period": "Oct 2022 - Estimated 2026"
    },
    {
      "school": "FPT Polytechnic College",
      "major": "Software Technology",
      "period": "Sep 2016 - Jun 2019"
    }
  ],
  "certificates": [
    {
      "name": "Google Project Management Specialization",
      "issuer": "Google, on Coursera",
      "date": "June 2026",
      "url": "https://coursera.org/share/2911d1c95f243b3ebc10cf25cfff62d2"
    }
  ],
  "experiences": [
    {
      "title": "Full-stack Developer (Full-time)",
      "company": "Presight Co., Ltd",
      "period": "Oct 2022 - Present",
      "description": [
        "Presight (2022 - Present): developed D3.js visualizations (Bar, Combo, Donut, Line, KPI, Waterfall, Bubble, Tree charts) for complex data scenarios",
        "Built full-stack integration modules connecting SQL/NoSQL databases, SaaS tools (Salesforce, HubSpot, QuickBooks, Xero), data lakehouses (Dremio) and spreadsheets (Google Sheets, Zoho Sheet)",
        "Built scalable ETL pipelines with Airbyte on GCP (550+ connectors) for real-time and scheduled sync to BigQuery",
        "Developed team management features: roles and permissions, secure token exchange, real-time collaboration",
        "Implemented a Stripe subscription and billing system (plans, add-ons, trials, upgrades/downgrades, cancellations) synced through webhooks",
        "Implemented a real-time AI assistant interface with reasoning steps, sources, charts and tables, plus chat thread and history management",
        "Reviewed and approved 100+ merge requests to keep code quality and standards consistent",
        "EvePM (2025): engineered a cross-platform productivity assistant (Electron.js, React Native) with audio recording and AI summarization, integrated with Notion, Jira, Asana, Gmail, Google Calendar and Slack",
        "Stacks: TypeScript, React, D3.js, Chakra UI, Storybook, Node.js (NestJS), Python (Flask), MongoDB, Postgres, BigQuery, WebSocket, Redis, RabbitMQ, Airbyte, Kong Gateway; Infrastructure: EC2, S3, GCP"
      ]
    },
    {
      "title": "Front-end Developer (Full-time)",
      "company": "Sieu Viet Group",
      "period": "Oct 2020 - Sep 2022",
      "description": [
        "Vieclam24h.vn: maintained and developed three job platforms (vieclam24h.vn, timviecnhanh.com, mywork.com.vn)",
        "Raised PageSpeed Insights score from 75 to 98 with code-splitting, image compression, caching and critical CSS",
        "Built fully responsive layouts across desktop, tablet and mobile",
        "Developed UTM tracking parameter features (utm_source, utm_medium, utm_campaign) for affiliate campaigns with KOLs and partners",
        "Applicant Tracking System: responsive UI with drag-and-drop candidate status management, data integration, authentication and Google Sign-In",
        "CV Builder: authentication with Google Sign-In, resume layout building and template switching; 1,000+ active users during the 2020 launch",
        "Stacks: React.js, NextJS, VueJS, Tailwind CSS, SCSS, Node.js (NestJS), MongoDB; Infrastructure: EC2, S3"
      ]
    },
    {
      "title": "Front-end Developer (Full-time)",
      "company": "NTH Software Solutions",
      "period": "Oct 2019 - Oct 2020",
      "description": [
        "Maintained and upgraded a clinic management system for patient record handling",
        "Implemented barcode scanning to auto-fill patient information and streamline medical forms",
        "Supported printing of medical records and examination slips by department",
        "Built a department-based queue system with automatic numbering shown on an electronic board",
        "Integrated SMS notifications for appointment reminders and result updates",
        "Stacks: JavaScript, React, REST API, Barcode Scanning, SMS Gateway"
      ]
    },
    {
      "title": "Intern (Full-time)",
      "company": "FPT Software",
      "period": "Apr 2019 - Jun 2019",
      "description": [
        "Practiced Agile methodologies (Scrum) and participated in requirement gathering and project scoping with internal stakeholders",
        "Gained hands-on experience in JavaScript and front-end development",
        "Assisted in developing UI components for web applications",
        "Took part in daily stand-ups and the team's agile development process",
        "Worked on bug fixes and feature implementation under senior guidance"
      ]
    }
  ],
  "skillCategories": [
    {
      "id": "frontend",
      "name": "Front-end",
      "skills": [
        { "name": "TypeScript", "at": ["presight"] },
        { "name": "JavaScript", "at": ["sieuviet", "nth", "fpt"] },
        { "name": "HTML", "at": ["fpt"] },
        { "name": "CSS", "at": ["fpt"] },
        { "name": "React", "at": ["presight", "sieuviet", "nth"] },
        { "name": "Next.js", "at": ["sieuviet"] },
        { "name": "Vue.js", "at": ["sieuviet"] },
        { "name": "Electron", "at": ["presight"] },
        { "name": "Tailwind CSS", "at": ["presight", "sieuviet"] },
        { "name": "Chakra UI", "at": ["presight"] },
        { "name": "Ant Design", "at": ["presight"] },
        { "name": "SCSS", "at": ["sieuviet"] }
      ]
    },
    {
      "id": "backend",
      "name": "Back-end",
      "skills": [
        { "name": "Node.js (Express, NestJS)", "at": ["presight", "sieuviet"] },
        { "name": "Python", "at": ["presight"] },
        { "name": "FastAPI" },
        { "name": "SQL" },
        { "name": "RESTful APIs", "at": ["nth"] },
        { "name": "WebSocket", "at": ["presight"] },
        { "name": "JWT / OAuth2", "at": ["presight", "sieuviet"] },
        { "name": "PostgreSQL", "at": ["presight"] },
        { "name": "MySQL" },
        { "name": "MSSQL" },
        { "name": "MongoDB", "at": ["presight", "sieuviet"] },
        { "name": "Redis", "at": ["presight"] }
      ]
    },
    {
      "id": "data",
      "name": "Data & Integration",
      "skills": [
        { "name": "ETL pipelines", "at": ["presight"] },
        { "name": "Data synchronization", "at": ["presight"] },
        { "name": "Stripe", "at": ["presight"] },
        { "name": "Salesforce", "at": ["presight"] },
        { "name": "HubSpot", "at": ["presight"] },
        { "name": "QuickBooks", "at": ["presight"] },
        { "name": "Google APIs", "at": ["presight", "sieuviet"] },
        { "name": "Slack", "at": ["presight"] },
        { "name": "Jira", "at": ["presight"] },
        { "name": "Notion", "at": ["presight"] }
      ]
    },
    {
      "id": "cicd",
      "name": "Cloud & DevOps",
      "skills": [
        { "name": "GCP", "at": ["presight"] },
        { "name": "AWS", "at": ["presight", "sieuviet"] },
        { "name": "Docker" },
        { "name": "Kubernetes" },
        { "name": "Kong Gateway", "at": ["presight"] },
        { "name": "GitHub Actions" },
        { "name": "RabbitMQ", "at": ["presight"] }
      ]
    },
    {
      "id": "other",
      "name": "Soft Skills & Languages",
      "skills": [
        { "name": "Communication" },
        { "name": "Leadership (led university teams 3+ years)" },
        { "name": "Time Management" },
        { "name": "Scrum (Agile)", "at": ["fpt"] },
        { "name": "Vietnamese: Native" },
        { "name": "English: Proficient reading/writing, basic speaking" }
      ]
    }
  ],
  "projects": [
    {
      "title": "Presight",
      "description": "Data analytics platform with D3.js visualizations, 550+ connector ETL pipelines, Stripe billing and a real-time AI assistant",
      "technologies": ["React", "TypeScript", "D3.js", "NestJS", "MongoDB", "Postgres", "BigQuery", "Airbyte"]
    },
    {
      "title": "EvePM",
      "description": "Cross-platform productivity assistant with audio recording, AI summarization and Notion, Jira, Asana, Gmail, Calendar and Slack integrations",
      "technologies": ["Electron.js", "React Native", "NestJS", "Flask", "MongoDB", "Redis"]
    },
    {
      "title": "CV Builder",
      "description": "Resume builder with customizable templates and Google Sign-In; 1,000+ active users during the 2020 launch",
      "technologies": ["React.js", "NextJS", "NestJS", "MongoDB", "Tailwind CSS"]
    },
    {
      "title": "Applicant Tracking System",
      "description": "Recruitment management UI with drag-and-drop candidate status management and Google Sign-In",
      "technologies": ["Vue.js", "NestJS", "MongoDB", "Tailwind CSS"]
    }
  ],
  "contact": {
    "email": "duyhp2405@gmail.com",
    "linkedin": "https://linkedin.com/in/duyhp",
    "github": "https://github.com/duyhp2405",
    "phone": "+84337934747",
    "website": "https://www.duyhuynh.codes/",
    "resume": "https://drive.google.com/file/d/116Ab_sxuiAkx4U-AQ_Hic9oC_W3niZy9/view?usp=sharing"
  }
}
